import { compareInstantsAtMicrosecondPrecision } from "../../lib/temporalInstant";

const AWARE_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})$/;
const AWARE_TIMESTAMP_PARTS = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|(([+-])(\d{2}):(\d{2})))$/;
const UTC_TIMESTAMP = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d{6})Z$/;
const SNAPSHOT_TOKEN = /^ts1\.[0-9a-f]{16}\.[A-Za-z0-9_-]{39,4060}$/;

export interface TemporalSnapshotCut {
  effectiveAsOf: string;
  recordedAsOf: string;
  snapshotToken: string;
}

export interface EffectiveAsOfInput {
  raw: string;
  normalized: string | null;
  error: string | null;
  /** Empty means the caller is asking the server to freeze its current clock. */
  requestsNow: boolean;
}

export interface TemporalReplayInput extends EffectiveAsOfInput {
  recordedAsOfInput: string | null;
  recordedAsOf: string | null;
  snapshotTokenInput: string | null;
  snapshotToken: string | null;
}

export interface TemporalCompareWindowInput {
  beforeRaw: string;
  before: string | null;
  afterRaw: string;
  after: string | null;
  error: string | null;
}

function validTimestampComponents(value: string): boolean {
  const match = AWARE_TIMESTAMP_PARTS.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] === "Z" ? 0 : Number(match[11]);
  const offsetMinute = match[8] === "Z" ? 0 : Number(match[12]);
  const boundaryYearUsesUtc = (year !== 1 && year !== 9999) || match[8] === "Z";
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthLengths = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const normalizedMillis = Date.parse(value);
  const normalizedUtcYear = Number.isFinite(normalizedMillis) ? new Date(normalizedMillis).getUTCFullYear() : 0;
  return year >= 1
    && month >= 1 && month <= 12
    && day >= 1 && day <= monthLengths[month - 1]!
    && hour <= 23 && minute <= 59 && second <= 59
    && offsetHour <= 23 && offsetMinute <= 59
    && boundaryYearUsesUtc
    && normalizedUtcYear >= 1 && normalizedUtcYear <= 9999;
}

function validTimestamp(value: string, pattern: RegExp): RegExpExecArray | null {
  const match = pattern.exec(value);
  return match && validTimestampComponents(value) ? match : null;
}

/**
 * Convert an aware RFC 3339 instant to the exact UTC/microsecond form used in
 * Thread deep links. The backend accepts arbitrary RFC 3339 fractional
 * precision and resolves it to PostgreSQL microseconds, so the URL codec does
 * the same deterministic truncation.
 */
export function normalizeEffectiveAsOf(value: string): string {
  const trimmed = value.trim();
  const match = validTimestamp(trimmed, AWARE_TIMESTAMP);
  if (!match) throw new Error("Enter an RFC 3339 timestamp with Z or an explicit offset.");
  const fraction = match[2] ?? "";
  const micros = fraction.slice(0, 6).padEnd(6, "0");
  const wholeSecondMillis = Date.parse(`${match[1]}${match[3]}`);
  if (!Number.isFinite(wholeSecondMillis)) throw new Error("Enter a real calendar timestamp.");
  const wholeSecondUtc = new Date(wholeSecondMillis).toISOString().slice(0, 19);
  return `${wholeSecondUtc}.${micros}Z`;
}

export function parseEffectiveAsOfInput(raw: string): EffectiveAsOfInput {
  if (!raw.trim()) return { raw, normalized: null, error: null, requestsNow: true };
  try {
    return { raw, normalized: normalizeEffectiveAsOf(raw), error: null, requestsNow: false };
  } catch (error) {
    return {
      raw,
      normalized: null,
      error: error instanceof Error ? error.message : "Enter a valid temporal cut.",
      requestsNow: false,
    };
  }
}

/** Strict server-cut validation; server values must already be UTC. */
export function isExactUtcInstant(value: unknown): value is string {
  if (typeof value !== "string") return false;
  return validTimestamp(value, UTC_TIMESTAMP) !== null;
}

export function isSnapshotToken(value: unknown): value is string {
  return typeof value === "string" && SNAPSHOT_TOKEN.test(value);
}

export function parseTemporalReplayInput(
  effectiveInput: string,
  recordedAsOfInput: string | null,
  snapshotTokenInput: string | null,
): TemporalReplayInput {
  const effective = parseEffectiveAsOfInput(effectiveInput);
  const recordedAsOf = recordedAsOfInput && isExactUtcInstant(recordedAsOfInput) ? recordedAsOfInput : null;
  const snapshotToken = snapshotTokenInput && isSnapshotToken(snapshotTokenInput) ? snapshotTokenInput : null;
  let error = effective.error;
  if (recordedAsOfInput !== null && recordedAsOf === null) {
    error = "Recorded/system time must be an exact UTC timestamp with six fractional digits.";
  } else if (snapshotTokenInput !== null && snapshotToken === null) {
    error = "Snapshot token is malformed or outside its bounded size.";
  } else if ((recordedAsOfInput === null) !== (snapshotTokenInput === null)) {
    error = "Recorded/system time and snapshot token must be supplied together.";
  } else if (recordedAsOf !== null && effective.normalized === null) {
    error = "A frozen replay also requires a valid effective/event time.";
  } else if (
    recordedAsOf !== null
    && effective.normalized !== null
    && compareInstantsAtMicrosecondPrecision(effective.normalized, recordedAsOf) > 0
  ) {
    error = "Effective/event time must not be later than recorded/system time.";
  }
  return {
    ...effective,
    error,
    recordedAsOfInput,
    recordedAsOf,
    snapshotTokenInput,
    snapshotToken,
  };
}

/** Parse the two business-effective instants used by the Time Compare view. */
export function parseTemporalCompareWindowInput(
  beforeRaw: string,
  afterRaw: string,
  recordedAsOf: string | null = null,
): TemporalCompareWindowInput {
  const before = parseEffectiveAsOfInput(beforeRaw);
  const after = parseEffectiveAsOfInput(afterRaw);
  let error: string | null = null;
  if (!beforeRaw.trim() && !afterRaw.trim()) {
    error = null;
  } else if (before.error) {
    error = `Before: ${before.error}`;
  } else if (after.error) {
    error = `After: ${after.error}`;
  } else if (before.normalized === null || after.normalized === null) {
    error = "Time Compare requires both a before and an after effective/event time.";
  } else if (compareInstantsAtMicrosecondPrecision(before.normalized, after.normalized) >= 0) {
    error = "Before effective/event time must be earlier than after effective/event time.";
  } else if (
    recordedAsOf !== null
    && compareInstantsAtMicrosecondPrecision(after.normalized, recordedAsOf) > 0
  ) {
    error = "After effective/event time must not be later than the active recorded/system cut.";
  }
  return {
    beforeRaw,
    before: before.normalized,
    afterRaw,
    after: after.normalized,
    error,
  };
}

export function temporalCutKey(cut: TemporalSnapshotCut | null): string | null {
  return cut ? JSON.stringify([cut.effectiveAsOf, cut.recordedAsOf, cut.snapshotToken]) : null;
}

export function sameTemporalCut(left: TemporalSnapshotCut | null, right: TemporalSnapshotCut | null): boolean {
  return temporalCutKey(left) === temporalCutKey(right);
}

export function abbreviatedSnapshotToken(token: string): string {
  if (token.length <= 26) return token;
  return `${token.slice(0, 13)}…${token.slice(-8)}`;
}
